import { baseApi } from '@/features/api/baseApi';
import directus, { type DirectusErrorType, type RequestQuery } from '@/features/api/directus';
import {
  type ReadOneQueryArg,
  buildCountFn,
  buildCreateFn,
  buildDeleteFn,
  buildDeleteManyFn,
  buildReadManyFn,
  buildReadOneFn,
  buildUpdateFn,
  buildUpdateManyFn,
} from '@/features/api/endpoints/createDirectusEndpoints';
import type { DigdashUserConfig } from '@/models/types';
import { triggerFlow } from '@directus/sdk';

const NEW_AUTH_TOKEN_FLOW_ID = import.meta.env.VITE_NEWAUTHTOKEN_FLOW_ID as string;

const digdashUserConfigApi = baseApi
  .enhanceEndpoints({
    addTagTypes: ['digdashUserConfig', 'digdashUserConfigs'],
  })
  .injectEndpoints({
    endpoints: builder => ({
      getDigdashUserConfigs: builder.query<DigdashUserConfig[], RequestQuery<DigdashUserConfig>>({
        queryFn: buildReadManyFn<DigdashUserConfig>('DigdashUserConfig'),
        providesTags: ['digdashUserConfigs'],
      }),
      getDigdashUserConfig: builder.query<
        DigdashUserConfig,
        ReadOneQueryArg<DigdashUserConfig, number>
      >({
        queryFn: buildReadOneFn<DigdashUserConfig, number>('DigdashUserConfig'),
        providesTags: ['digdashUserConfig'],
      }),
      getDigdashUserConfigCount: builder.query<number, RequestQuery<DigdashUserConfig> | undefined>(
        {
          queryFn: buildCountFn<DigdashUserConfig>('DigdashUserConfig'),
          providesTags: ['digdashUserConfigs'],
        },
      ),
      createDigdashUserConfig: builder.mutation<DigdashUserConfig, Partial<DigdashUserConfig>>({
        queryFn: buildCreateFn<DigdashUserConfig>('DigdashUserConfig'),
        invalidatesTags: ['digdashUserConfig', 'digdashUserConfigs'],
      }),
      updateDigdashUserConfig: builder.mutation<
        DigdashUserConfig,
        { id: number; body: Partial<DigdashUserConfig> }
      >({
        queryFn: buildUpdateFn<DigdashUserConfig, number>('DigdashUserConfig'),
        invalidatesTags: ['digdashUserConfig', 'digdashUserConfigs'],
      }),
      updateDigdashUserConfigs: builder.mutation<
        DigdashUserConfig[],
        { ids: number[]; body: Partial<DigdashUserConfig> }
      >({
        queryFn: buildUpdateManyFn<DigdashUserConfig, number>('DigdashUserConfig'),
        invalidatesTags: ['digdashUserConfig', 'digdashUserConfigs'],
      }),
      deleteDigdashUserConfig: builder.mutation<void, number>({
        queryFn: buildDeleteFn<number>('DigdashUserConfig'),
        invalidatesTags: ['digdashUserConfig', 'digdashUserConfigs'],
      }),
      deleteDigdashUserConfigs: builder.mutation<void, number[]>({
        queryFn: buildDeleteManyFn<number>('DigdashUserConfig'),
        invalidatesTags: ['digdashUserConfig', 'digdashUserConfigs'],
      }),
      getNewAuthToken: builder.query<{ newAuthTokenAs: string; user: string }, void>({
        queryFn: async () => {
          try {
            const data = await directus.request<{ newAuthTokenAs: string; user: string }>(
              triggerFlow('GET', NEW_AUTH_TOKEN_FLOW_ID),
            );
            return { data };
          } catch (error: unknown) {
            return { error: error as DirectusErrorType };
          }
        },
        keepUnusedDataFor: 3600, // 1 hour
      }),
    }),
  });

export const {
  useGetDigdashUserConfigsQuery,
  useGetDigdashUserConfigQuery,
  useGetDigdashUserConfigCountQuery,
  useCreateDigdashUserConfigMutation,
  useUpdateDigdashUserConfigMutation,
  useUpdateDigdashUserConfigsMutation,
  useDeleteDigdashUserConfigMutation,
  useDeleteDigdashUserConfigsMutation,
  useGetNewAuthTokenQuery,
} = digdashUserConfigApi;
